import { useEffect, useMemo, useState } from "react"
import { MapPin } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuthContext } from "@/contexts/AuthContext"
import { useCountries, useRegions, useLocations } from "@/graphql/hooks/Locations/useLocations"
import { useAddressConfirm } from "@/graphql/hooks/useAuth"
import { useToast } from "@/hooks/use-toast"

const THREE_MONTHS_MS = 1000 * 60 * 60 * 24 * 90

const isAddressStale = (modified?: string) => {
  if (!modified) return false
  const modifiedDate = new Date(modified)
  if (Number.isNaN(modifiedDate.getTime())) return false
  return Date.now() - modifiedDate.getTime() > THREE_MONTHS_MS
}

// countryId=1/regionId=1/locationId=1 era el valor por defecto que quedaba
// guardado por un bug del formulario de registro, nunca una dirección real.
const isDefaultJunkAddress = (a?: { countryId: number; regionId: number; locationId: number }) =>
  !!a && a.countryId === 1 && a.regionId === 1 && a.locationId === 1

const AddressConfirmModal = () => {
  const { isAuthenticated, loading: authLoading, person, refetch } = useAuthContext()
  const { confirmAddress, loading: confirming } = useAddressConfirm()
  const { toast } = useToast()

  const [open, setOpen] = useState(false)
  const [countryId, setCountryId] = useState(0)
  const [regionId, setRegionId] = useState(0)
  const [locationId, setLocationId] = useState(0)

  const address = person?.Address

  useEffect(() => {
    if (!authLoading && isAuthenticated && person) {
      const needsAddress = !address || isDefaultJunkAddress(address)
      const needsConfirmation = needsAddress || isAddressStale(address?.modified)

      if (needsConfirmation) {
        if (needsAddress) {
          setCountryId(0)
          setRegionId(0)
          setLocationId(0)
        } else {
          setCountryId(address?.countryId || 0)
          setRegionId(address?.regionId || 0)
          setLocationId(address?.locationId || 0)
        }
        setOpen(true)
      }
    }
  }, [authLoading, isAuthenticated, person, address])

  const { countries } = useCountries()
  const { regions } = useRegions(countryId)
  const { locations, refetchLocations } = useLocations({ regionId })

  useEffect(() => {
    if (regionId) refetchLocations(regionId)
  }, [regionId])

  const canSubmit = useMemo(() => {
    if (!countryId || !regionId) return false
    if (locations.length > 0 && !locationId) return false
    return true
  }, [countryId, regionId, locationId, locations])

  const handleSubmit = async () => {
    if (!canSubmit) return
    try {
      const result = await confirmAddress({
        countryId,
        regionId,
        locationId: locationId || undefined,
      })
      if (result?.status.success) {
        toast({
          title: "¡Gracias!",
          description: "Tu dirección fue actualizada correctamente.",
          variant: "success",
        })
        setOpen(false)
        refetch?.()
      } else {
        toast({
          title: "Error",
          description: result?.status.detail || "No se pudo actualizar la dirección.",
          variant: "destructive",
        })
      }
    } catch {
      // El error ya se notifica en el hook useAddressConfirm.
    }
  }

  if (!open) return null

  return (
    <Dialog open={open}>
      <DialogContent
        hideCloseButton
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        className="max-w-md"
      >
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <MapPin className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center">Confirma tu dirección</DialogTitle>
          <DialogDescription className="text-center">
            Para mantener tus datos al día, confirma o actualiza tu país, región y comuna.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <label className="text-sm font-medium">País</label>
            <Select
              value={countryId ? countryId.toString() : undefined}
              onValueChange={(value) => {
                const newCountryId = parseInt(value)
                setCountryId(newCountryId)
                setRegionId(0)
                setLocationId(0)
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar país" />
              </SelectTrigger>
              <SelectContent>
                {countries.map((country) => (
                  <SelectItem key={country.id} value={country.id.toString()}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <label className="text-sm font-medium">Región</label>
            <Select
              value={regionId ? regionId.toString() : undefined}
              onValueChange={(value) => {
                const newRegionId = parseInt(value)
                setRegionId(newRegionId)
                setLocationId(0)
              }}
              disabled={!countryId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar región" />
              </SelectTrigger>
              <SelectContent>
                {regions.map((region) => (
                  <SelectItem key={region.id} value={region.id.toString()}>
                    {region.fullName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {locations.length > 0 && (
            <div className="grid gap-2">
              <label className="text-sm font-medium">Comuna</label>
              <Select
                value={locationId ? locationId.toString() : undefined}
                onValueChange={(value) => setLocationId(parseInt(value))}
                disabled={!regionId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar comuna" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectItem key={location.id} value={location.id.toString()}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Button onClick={handleSubmit} disabled={!canSubmit || confirming} className="w-full">
            {confirming ? "Guardando..." : "Confirmar dirección"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AddressConfirmModal
